// Niveau 4 — Bund og tegn (c x z , . -)

import type { CurriculumTier, Trin } from '../../types';
import { NIVEAU_1_SPRINT_WORDS } from './niveau-1';
import { NIVEAU_2_SPRINT_WORDS } from './niveau-2';
import { NIVEAU_3_SPRINT_WORDS, NIVEAU_3_UNLOCKED_KEYS } from './niveau-3';
import {
  filterWords,
  fromChunks,
  fromLetters,
  localizedFromChunks,
  localizedInterleaved,
  localizedLiteral,
  round,
} from './generators';
import { consolidationTrin, pairTrin } from './trinBuilders';
import {
  NIVEAU_1_EN_SPRINT_WORDS,
  NIVEAU_2_EN_SPRINT_WORDS,
  NIVEAU_3_EN_SPRINT_WORDS,
  NIVEAU_4_CONSOLIDATION_SENTENCE_EN,
  NIVEAU_4_EN_ALPHABET_WORDS,
  NIVEAU_4_FINAL_SENTENCE_EN,
  NIVEAU_4_LIST_SENTENCE_EN,
  NIVEAU_4_PERIOD_WORDS_EN,
  NIVEAU_4_PUNCTUATION_SENTENCE_EN,
} from './niveau-4-en';

let prior = [...NIVEAU_3_UNLOCKED_KEYS];

const t1 = pairTrin(
  'niveau-4',
  1,
  'C og X',
  'C og X med lang- og ringfinger.',
  'Stræk lang- og ringfinger ned til C og X.',
  ['c', 'x'],
  prior,
  {
    intro: 'c x c x cx xc c x c x cx xc',
    connect: 'sc lx dc kx sc lx dc kx sc lx',
    recall1: 'cx sc lx dc kx cx sc lx dc',
    recall2: 'xc sc lx dc kx xc sc lx dc',
    challenge: 'sclx dckx cxsc lxdc',
  },
  { palette: 'echo' },
);
prior = t1.unlockedKeys;

const t2 = pairTrin(
  'niveau-4',
  2,
  'Z og komma',
  'Z og komma med lillefingeren.',
  'Ram Z og komma uden at flytte hænderne.',
  ['z', ','],
  prior,
  {
    intro: 'z , z , z, ,z z , z , z, ,z',
    connect: 'sz l, dz k, sz l, dz k, sz l,',
    recall1: 'z, sz l, dz k, z, sz l, dz',
    recall2: ',z sz l, dz k, ,z sz l, dz',
    challenge: 'sz,l, dz,k, z,sz l,dz',
  },
  { palette: 'echo' },
);
prior = t2.unlockedKeys;

const t3 = pairTrin(
  'niveau-4',
  3,
  'Punktum og bindestreg',
  'Afslut ord og lav bindestreg.',
  'Brug punktum og bindestreg i korte sætninger.',
  ['.', '-'],
  prior,
  {
    intro: '. - . - .- -. . - . - .- -.',
    connect: 's. d- f. j- k. l- s. d- f. j-',
    recall1: '.- s. d- f. j- k. l- .- s.',
    recall2: '-. s. d- f. j- k. l- -. s.',
    challenge: 'ord. test- linje. kort-',
  },
  { palette: 'echo' },
);
prior = t3.unlockedKeys;

export const NIVEAU_4_ALPHABET_WORDS = filterWords(
  [
    'side', 'med', 'og', 'det', 'er', 'en', 'til', 'på',
    'for', 'han', 'hun', 'vi', 'de', 'jeg', 'du', 'skal',
    'kan', 'har', 'var', 'god', 'ny', 'stor', 'lille', 'dag',
    'nat', 'bog', 'ord', 'tekst', 'skrive', 'læse', 'taste', 'finger',
    'hånd', 'øve', 'træne', 'sand', 'start', 'mellem', 'skole', 'land',
    'mand', 'kvinde', 'verden', 'arbejde', 'arbejder', 'familie', 'stadig', 'altid',
    'meget', 'lidt', 'helt', 'sige', 'siger', 'komme', 'vand', 'ting',
    'sted', 'liv', 'fri', 'skov', 'sprog', 'børn', 'have', 'bliver',
    'første', 'anden', 'samme', 'hele', 'store', 'bruge', 'bruger', 'slut',
    'grund', 'find', 'fundet', 'stedet', 'mark', 'værk', 'væk', 'bænk',
    'æble', 'øje',

    // Flere c/x/z-ord (så Niveau 4 faktisk føles som Niveau 4)
    'center', 'chance', 'citat', 'cirkus', 'cirkel', 'citron', 'cykel', 'cyklus',
    'zone', 'zoo', 'zebra', 'zink', 'pizza',
    'fax', 'max', 'mix', 'fix', 'taxa',

    'ekstra', 'problem', 'projekt',
    'praksis', 'præcis', 'prøve', 'skæbne', 'skærm', 'træ', 'træer', 'øvelse',
    'dansk', 'lærer', 'elev', 'klasse', 'time', 'opgave', 'svar', 'natur',
    'bygning', 'vej', 'gade', 'vinter', 'sommer', 'kraft', 'kræfter', 'lektion',
    'timer', 'opgaven', 'svaret', 'huset', 'husene', 'bygningen', 'vejen', 'gaden',
    'læreren', 'eleven', 'klassen',
    // Navne (dictation-names.json)
    'axel',
  ],
  prior,
);

/** Korte ord med punktum — korrekt afslutning. */
export const NIVEAU_4_PERIOD_WORDS_DA = 'kat. bog. ord. dag. det er godt.';

/** Kort liste med komma. */
export const NIVEAU_4_LIST_SENTENCE_DA = 'zone, zoo, citron og cykel.';

export const NIVEAU_4_PUNCTUATION_SENTENCE_DA =
  'axel, du kan mixe ord. skriv en ekstra-tekst. zone, zoo.';

export const NIVEAU_4_CONSOLIDATION_SENTENCE_DA =
  'jeg kan skrive ord nu, og det er godt.';

const NIVEAU_4_FINAL_SENTENCE_DA =
  'ti finger skrivning er god træning for fingrene';

const t4Base = consolidationTrin(
  'niveau-4',
  4,
  'Konsolidering',
  'Bland alle bogstaver i korte ord og sætninger.',
  'Skriv rigtige ord med hele alfabetet.',
  prior,
  NIVEAU_4_ALPHABET_WORDS,
  NIVEAU_4_CONSOLIDATION_SENTENCE_DA,
  {
    lexicon: {
      wordsEn: NIVEAU_4_EN_ALPHABET_WORDS,
      sentenceEn: NIVEAU_4_CONSOLIDATION_SENTENCE_EN,
    },
  },
);

const t4: Trin = {
  ...t4Base,
  rounds: [
    ...t4Base.rounds,
    round({
      id: 'niveau-4-trin-4-r7a',
      title: 'Ord og punktum',
      purpose: 'Afslut korte ord med punktum og mellemrum.',
      mode: 'recall',
      generate: localizedLiteral(
        NIVEAU_4_PERIOD_WORDS_DA,
        NIVEAU_4_PERIOD_WORDS_EN,
      ),
    }),
    round({
      id: 'niveau-4-trin-4-r7b',
      title: 'Liste med komma',
      purpose: 'Komma mellem ord — og før det sidste.',
      mode: 'recall',
      generate: localizedLiteral(
        NIVEAU_4_LIST_SENTENCE_DA,
        NIVEAU_4_LIST_SENTENCE_EN,
      ),
    }),
    round({
      id: 'niveau-4-trin-4-r7c',
      title: 'Tegn i sætninger',
      purpose: 'Komma, punktum og bindestreg i korte sætninger.',
      mode: 'recall',
      generate: localizedLiteral(
        NIVEAU_4_PUNCTUATION_SENTENCE_DA,
        NIVEAU_4_PUNCTUATION_SENTENCE_EN,
      ),
    }),
    round({
      id: 'niveau-4-trin-4-r7',
      title: 'Bland med alle tidligere niveauer',
      purpose: 'Ord på tværs af Niveau 1–4.',
      mode: 'recall',
      generate: localizedInterleaved(
        [
          { chunks: NIVEAU_1_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_2_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_3_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_4_ALPHABET_WORDS, weight: 2 },
        ],
        [
          { chunks: NIVEAU_1_EN_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_2_EN_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_3_EN_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_4_EN_ALPHABET_WORDS, weight: 2 },
        ],
        100,
      ),
    }),
    round({
      id: 'niveau-4-trin-4-r8',
      title: 'Flash-genkald',
      purpose: 'Se ordet kort, skriv det fra hukommelse.',
      mode: 'recall',
      generate: localizedFromChunks(
        NIVEAU_4_ALPHABET_WORDS,
        NIVEAU_4_EN_ALPHABET_WORDS,
        50,
      ),
      presentation: { kind: 'echo', flashMs: 1500, unit: 'word' },
    }),
  ],
};

const t5: Trin = {
  id: 'niveau-4-trin-5',
  tierId: 'niveau-4',
  trinNumber: 5,
  title: 'Hele tastaturet',
  description: 'Afsluttende udfordring for bogstaver.',
  goal: 'Bevis at du kan alle bogstaver med præcision.',
  unlockedKeys: prior,
  newKeys: [],
  rounds: [
    round({ id: 'niveau-4-trin-5-r1', title: 'Opvarmning', purpose: 'Rolig start.', mode: 'guided', generate: fromChunks(['asdf', 'jklø', 'qwert', 'uiopå', 'zxcv', 'bnm,.'], 60) }),
    round({
      id: 'niveau-4-trin-5-r2',
      title: 'Ord-blanding',
      purpose: 'Varieret ordforråd.',
      mode: 'recall',
      generate: localizedFromChunks(
        NIVEAU_4_ALPHABET_WORDS,
        NIVEAU_4_EN_ALPHABET_WORDS,
        100,
      ),
    }),
    round({
      id: 'niveau-4-trin-5-r3',
      title: 'Sætning',
      purpose: 'En kort sætning.',
      mode: 'recall',
      generate: localizedLiteral(
        NIVEAU_4_FINAL_SENTENCE_DA,
        NIVEAU_4_FINAL_SENTENCE_EN,
      ),
    }),
    round({ id: 'niveau-4-trin-5-r4', title: 'Genkald', purpose: 'Uden mønster.', mode: 'recall', generate: fromLetters(prior.filter((k) => k !== ' '), 5, 90) }),
    round({
      id: 'niveau-4-trin-5-r5',
      title: 'Udfordring',
      purpose: 'Sidste lektion i bogstaver.',
      mode: 'challenge',
      generate: localizedFromChunks(
        NIVEAU_4_ALPHABET_WORDS,
        NIVEAU_4_EN_ALPHABET_WORDS,
        100,
      ),
    }),
    round({
      id: 'niveau-4-trin-5-r6',
      title: 'Diktat: hele sætningen',
      purpose: 'Lyt til sætningen og skriv den uden at se den.',
      mode: 'recall',
      generate: localizedLiteral(
        NIVEAU_4_FINAL_SENTENCE_DA,
        NIVEAU_4_FINAL_SENTENCE_EN,
      ),
      presentation: { kind: 'dictation' },
    }),
    round({
      id: 'niveau-4-trin-5-r7',
      title: 'Skygge — følg med teksten',
      purpose: 'Teksten ruller af sig selv. Hold tempo.',
      mode: 'challenge',
      generate: localizedFromChunks(
        NIVEAU_4_ALPHABET_WORDS,
        NIVEAU_4_EN_ALPHABET_WORDS,
        90,
      ),
      presentation: { kind: 'shadow', cps: 4 },
    }),
  ],
};

export const NIVEAU_4_UNLOCKED_KEYS = prior;

export const NIVEAU_4_TRINS: Trin[] = [t1, t2, t3, t4, t5];

export const NIVEAU_4_TIER: CurriculumTier = {
  id: 'niveau-4',
  title: 'Niveau 4',
  description: 'Bundrækken, tegn og hele alfabetet.',
  trins: NIVEAU_4_TRINS,
};
